import { FC, useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Customer, PaymentReportItem } from 'src/models/Management.interface';
import { getBasicCustomers } from 'src/services/customer.service';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useSnackbar } from 'notistack';
import { generatePaymentReportPDF } from 'src/components/Invoice/PaymentReportPDF';
import { generatePaymentReportCSV } from 'src/utils/csv.utils';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import MainLayout from 'src/pages/private';
import { routeURL } from 'src/utils/properties.utils';
import { useGetPaymentReport } from 'src/services/report.service';
import Tooltip from '@mui/material/Tooltip';
import {
  ReportPageWrapper,
  FilterPaper,
  FilterActionsBoxSmallGap,
  FlexSpacer,
  GenerateButton,
  PdfIconButton,
  CsvIconButton,
  LoadingBox,
  SummaryGrid,
  StatCard,
  StatLabel,
  StatValueBlue,
  StyledTableContainer,
  HeaderCell,
  DataCellBold,
  DataCellMedium,
  DataCellBlue,
  DataCell,
  EmptyCell,
  CustomerTextField,
  OrderLink,
} from './ReportPage.styled';

const PaymentReport: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { loading, reportData, fetchPaymentReport, error } =
    useGetPaymentReport();
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().startOf('month')
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const data = await getBasicCustomers();
        setCustomers(data);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      }
    };
    loadCustomers();
  }, []);

  const handleGenerateReport = useCallback(() => {
    if (startDate && endDate) {
      fetchPaymentReport({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        customerId: selectedCustomer?.id,
      });
    }
  }, [startDate, endDate, selectedCustomer, fetchPaymentReport]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  // Run once on mount
  useEffect(() => {
    fetchPaymentReport({
      startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    });
  }, [fetchPaymentReport]);

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null) return '0.00';
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <MainLayout title="Payment Report">
      <ReportPageWrapper>
        <FilterPaper elevation={0}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} sm={4} md={3}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={customers}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    return (
                      option.name ||
                      option.organizationName ||
                      ''
                    ).trim();
                  }}
                  value={selectedCustomer}
                  onChange={(_, newValue) => setSelectedCustomer(newValue)}
                  renderInput={(params) => (
                    <CustomerTextField
                      {...params}
                      label="Customer"
                      variant="outlined"
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <DatePicker
                  label="Date From"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <DatePicker
                  label="Date To"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <FilterActionsBoxSmallGap>
                  <GenerateButton
                    variant="contained"
                    onClick={handleGenerateReport}
                    disabled={loading}
                  >
                    Generate
                  </GenerateButton>
                  <FlexSpacer />
                  <Tooltip title="Export to PDF">
                    <span>
                      <PdfIconButton
                        onClick={() =>
                          reportData &&
                          generatePaymentReportPDF(reportData, {
                            startDate: startDate?.format('YYYY-MM-DD') || null,
                            endDate: endDate?.format('YYYY-MM-DD') || null,
                            customerName: selectedCustomer
                              ? selectedCustomer.name ||
                                selectedCustomer.organizationName
                              : null,
                          })
                        }
                        disabled={!reportData || loading}
                      >
                        <PictureAsPdfIcon fontSize="small" />
                      </PdfIconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Export to CSV">
                    <span>
                      <CsvIconButton
                        onClick={() =>
                          reportData &&
                          generatePaymentReportCSV(reportData, {
                            startDate: startDate?.format('YYYY-MM-DD') || null,
                            endDate: endDate?.format('YYYY-MM-DD') || null,
                            customerName: selectedCustomer
                              ? selectedCustomer.name ||
                                selectedCustomer.organizationName
                              : null,
                          })
                        }
                        disabled={!reportData || loading}
                      >
                        <DescriptionIcon fontSize="small" />
                      </CsvIconButton>
                    </span>
                  </Tooltip>
                </FilterActionsBoxSmallGap>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </FilterPaper>

        {loading ? (
          <LoadingBox>
            <CircularProgress />
          </LoadingBox>
        ) : (
          <>
            <SummaryGrid container spacing={3}>
              <Grid item xs={12} sm={6} lg={4}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    TOTAL RECEIVED
                  </StatLabel>
                  <StatValueBlue variant="h4">
                    {formatCurrency(reportData?.summary.totalPaidAmount || 0)}
                  </StatValueBlue>
                </StatCard>
              </Grid>
            </SummaryGrid>

            <StyledTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <HeaderCell>PAYMENT DATE</HeaderCell>
                    <HeaderCell>ORDER #</HeaderCell>
                    <HeaderCell>CUSTOMER</HeaderCell>
                    <HeaderCell align="right">PAID AMOUNT</HeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData?.payments.length === 0 ? (
                    <TableRow>
                      <EmptyCell colSpan={4} align="center">
                        <Typography color="var(--subtext)">
                          No payments found for the selected criteria.
                        </Typography>
                      </EmptyCell>
                    </TableRow>
                  ) : (
                    reportData?.payments.map((payment: PaymentReportItem) => (
                      <TableRow key={payment.id} hover>
                        <DataCell>
                          {dayjs(payment.paymentDate).format('DD/MM/YYYY')}
                        </DataCell>
                        <DataCellBold>
                          <OrderLink
                            to={routeURL.ORDER_PAYMENTS_REDIRECT(
                              payment.orderId
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {payment.orderNumber}
                          </OrderLink>
                        </DataCellBold>
                        <DataCell>{payment.customerName}</DataCell>
                        <DataCellBlue align="right">
                          {formatCurrency(payment.paidAmount)}
                        </DataCellBlue>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </>
        )}
      </ReportPageWrapper>
    </MainLayout>
  );
};

export default PaymentReport;
